import { useEffect, useRef } from 'react'
import classNames from 'classnames'
import isNull from 'lodash-es/isNull'

import {
  EMPTY_OBJECT,
  INPUT_AUTOCOMPLETE_VALUE,
} from '../../../constants/common'
import { EVENT_NAME_RESIZE } from '../../../constants/event'
import { useUserData } from '../../../hooks'
import { InputField } from '../../Input'
import { List } from '../../List'
import { ListDivider } from '../../ListDivider'
import {
  EmptyListItem,
  FilterTemplateListItem,
  LabelListItem,
  RatingListItem,
  RoleListItem,
  TextListItem,
  UserListItem,
} from '../../ListItem'
import { StatusListItem } from '../../StatusListItem'
import {
  useListKeyboardNavigation,
  useSelectDropdownScrollDismiss,
} from '../Select.hooks'

import { getAvatar } from './SelectList.utils'

import * as styles from './SelectList.module.css'

import SearchIcon from '../../../icons/search.svg'

function SelectList({
  ExtraItem,
  ListItem,
  activeItems,
  buttonType,
  entity,
  extraItemProps,
  getLabel,
  getListItemProps,
  getValue,
  isInitiated,
  isInputType,
  isLoading,
  isMulti,
  itemContentClassName,
  listIcon,
  listType,
  localSearch,
  maxHeight,
  menuClassName,
  noDataText,
  options,
  optionsCount,
  placeholder,
  scrollListener = true,
  search,
  searchKey,
  showListTooltip,
  onChangeSearch,
  onClick,
  onCloseMenu,
  onEdit,
  onScroll,
  onSetListHeight,
}) {
  const wrapperRef = useRef(null)

  const currentUser = useUserData()

  useSelectDropdownScrollDismiss({ scrollListener, wrapperRef, onCloseMenu })

  useEffect(() => {
    window.addEventListener(EVENT_NAME_RESIZE, onCloseMenu)

    return function cleanup() {
      window.removeEventListener(EVENT_NAME_RESIZE, onCloseMenu)
    }
  }, [])

  function handleClick(option) {
    onClick(option)

    if (!isMulti) {
      onCloseMenu(option)
    }
  }

  function getHandleClick(option) {
    return () => handleClick(option)
  }

  const { focusedItemIndex, getHandleMouseEnter, handleMouseLeave } =
    useListKeyboardNavigation({
      initialFocusIndex: isInputType || (buttonType && searchKey) ? null : 0,
      listRef: wrapperRef,
      options,
      optionsCount,
      onClick: handleClick,
    })

  const listClassName = classNames(styles.SelectList, menuClassName)

  const showSearchElement =
    (buttonType && searchKey) || (localSearch && !isInputType)

  return (
    <List
      ExtraItem={ExtraItem}
      className={listClassName}
      extraItemProps={extraItemProps}
      isLoading={isLoading || (options.length === 0 && !isInitiated)} // TODO Remove loading for search cases
      maxHeight={maxHeight}
      searchElement={
        showSearchElement ? (
          <InputField
            autoFocus
            afterIconProps={{ Icon: SearchIcon }}
            autoComplete={INPUT_AUTOCOMPLETE_VALUE.off}
            height={32}
            placeholder={placeholder}
            value={search}
            wrapperClassName={styles.search}
            onChange={onChangeSearch}
          />
        ) : null
      }
      setListHeight={onSetListHeight}
      wrapperRef={wrapperRef}
      onScroll={onScroll}
    >
      {options.length === 0 && (isInitiated || localSearch) ? (
        <EmptyListItem text={noDataText} />
      ) : null}

      {options.map((option, index) => {
        if (isNull(option)) {
          return <ListDivider key='divider' />
        }

        const text = getLabel?.(option) ?? option.label
        const value = getValue?.(option) ?? option.value
        const link = option.link || null

        if (ListItem || option.ListItem) {
          const Component = ListItem || option.ListItem
          const getListOptionItemProps =
            getListItemProps || option.getListItemProps

          const {
            isMulti: isOptionItemMulti,
            text: optionItemText,
            value: optionItemValue,
            ...restOptionItemProps
          } = getListOptionItemProps?.(option) || EMPTY_OBJECT

          return (
            <Component
              {...restOptionItemProps}
              isActive={activeItems?.includes(value)}
              isFocused={focusedItemIndex === index}
              isMulti={isOptionItemMulti || isMulti}
              key={index}
              listIcon={listIcon}
              showTooltip={showListTooltip}
              text={optionItemText || text}
              value={optionItemValue || option}
              onClick={getHandleClick(option)}
              onMouseEnter={getHandleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            />
          )
        }

        if (listType === 'rating') {
          return (
            <RatingListItem
              isActive={activeItems?.includes(value)}
              isFocused={focusedItemIndex === index}
              isMulti={isMulti}
              key={index}
              value={option}
              onClick={getHandleClick(option)}
              onMouseEnter={getHandleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            />
          )
        }

        if (listType === 'role') {
          return (
            <RoleListItem
              isActive={activeItems?.includes(value)}
              isFocused={focusedItemIndex === index}
              isMulti={isMulti}
              key={index}
              showTooltip={showListTooltip}
              value={option}
              onClick={getHandleClick(option)}
              onMouseEnter={getHandleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            />
          )
        }

        if (listType === 'user') {
          return (
            <UserListItem
              {...getListItemProps?.(option)}
              currentUser={currentUser}
              isActive={activeItems?.includes(value)}
              isFocused={focusedItemIndex === index}
              isMulti={isMulti}
              key={index}
              showTooltip={showListTooltip}
              value={option}
              onClick={getHandleClick(option)}
              onMouseEnter={getHandleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            />
          )
        }

        if (listType === 'label') {
          return (
            <LabelListItem
              isActive={activeItems?.includes(value)}
              isFocused={focusedItemIndex === index}
              isMulti={isMulti}
              key={index}
              showTooltip={showListTooltip}
              value={option}
              onClick={getHandleClick(option)}
              onMouseEnter={getHandleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            />
          )
        }

        if (listType === 'status') {
          return (
            <StatusListItem
              entity={entity}
              isActive={activeItems?.includes(value)}
              isFocused={focusedItemIndex === index}
              isMulti={isMulti}
              item={option}
              key={index}
              status={value}
              onClick={getHandleClick(option)}
              onMouseEnter={getHandleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            />
          )
        }

        if (listType === 'filter') {
          return (
            <FilterTemplateListItem
              isArchived={option.label?.archived}
              isFocused={focusedItemIndex === index}
              isMulti={isMulti}
              key={index}
              text={text}
              value={option}
              onClick={getHandleClick(option)}
              onCloseMenu={onCloseMenu}
              onEdit={onEdit}
              onMouseEnter={getHandleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            />
          )
        }

        return (
          <TextListItem
            avatar={getAvatar(option)}
            contentClassName={itemContentClassName}
            disabled={option.disabled}
            isActive={activeItems?.includes(value)}
            isArchived={option.label?.archived}
            isFocused={focusedItemIndex === index}
            isMulti={isMulti}
            isPersistent={option.is_persistent}
            key={index}
            link={link}
            listIcon={listIcon}
            showTooltip={showListTooltip}
            text={text}
            tooltipContent={option.tooltipContent}
            value={value}
            onClick={getHandleClick(option)}
            onMouseEnter={getHandleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
          />
        )
      })}
    </List>
  )
}

export default SelectList
